import moment from 'moment';
import { ApiBind, Common } from './common.model';
import { AdFormatType, MediaType, DeliveryControlDrawMethod, PlatformReservationStatus } from '../constants';

/**
 * 配信PF予約登録に付属するdealを表すクラス.
 *
 * @class
 * @extends Common
 */
class PlatformReservationDeal extends Common {
  @ApiBind({ column: 'id', includeJSON: true })
  id: number;

  @ApiBind({ column: 'name', includeJSON: true })
  name: string;
}

/**
 * 配信PF予約登録に付属するデザインテンプレートを表すクラス.
 *
 * @class
 * @extends Common
 */
class PlatformReservationDesignTemplate extends Common {
  @ApiBind({ column: 'id', includeJSON: true })
  id: number;

  @ApiBind({ column: 'name', includeJSON: true })
  name: string;
}

/**
 * 配信PF予約登録に付属する配信コントロールを表すクラス.
 *
 * @class
 * @extends Common
 */
class PlatformReservationDeliveryControl extends Common {
  /**
   * @remarks 配信面とフォーマットによって必須/設定不可が異なる
   */
  @ApiBind({
    column: 'design_template',
    model: (value) => new PlatformReservationDesignTemplate(value),
    includeJSON: true
  })
  designTemplate?: PlatformReservationDesignTemplate;

  /**
   * @remarks Bidding配信PFかつDeal対応している配信PFの場合は任意、それ以外は設定不可
   */
  @ApiBind({
    column: 'deal',
    model: (value) => new PlatformReservationDeal(value),
    includeJSON: true
  })
  deal?: PlatformReservationDeal;

  /**
   * @remarks Network配信PFの場合のみ必須、それ以外は設定不可
   */
  @ApiBind({ column: 'ratio', includeJSON: true })
  ratio?: number;
}

/**
 * 配信PF予約登録に付属する配信セットを表すクラス.
 *
 * @class
 * @extends Common
 */
class PlatformReservationDeliverySet extends Common {
  @ApiBind({ column: 'id', includeJSON: true })
  id: number;

  @ApiBind({ column: 'name', includeJSON: true })
  name: string;

  @ApiBind({
    column: 'delivery_control',
    model: (value) => new PlatformReservationDeliveryControl(value),
    includeJSON: true
  })
  deliveryControl: PlatformReservationDeliveryControl;
}

/**
 * 配信PF予約登録に付属する広告リクエストを表すクラス.
 *
 * @class
 * @extends Common
 */
class PlatformReservationAdRequest extends Common {
  @ApiBind({ column: 'id', includeJSON: true })
  id: number;

  @ApiBind({ column: 'name', includeJSON: true })
  name: string;
}

/**
 * 配信PF予約登録に付属するプレースメントを表すクラス.
 *
 * @class
 * @extends Common
 */
class PlatformReservationPlacement extends Common {
  @ApiBind({ column: 'id', includeJSON: true })
  id: number;

  @ApiBind({ column: 'name', includeJSON: true })
  name: string;

  @ApiBind({ column: 'outer_placement_id', includeJSON: true })
  outerPlacementId: string;

  @ApiBind({
    column: 'ad_request',
    model: (value) => new PlatformReservationAdRequest(value),
    includeJSON: true
  })
  adRequest: PlatformReservationAdRequest;
}

/**
 * 配信PF予約登録に付属するプレースメント設定を表すクラス.
 *
 * @class
 * @extends Common
 */
class PlatformReservationPlacementRelation extends Common {
  @ApiBind({ column: 'id', includeJSON: true })
  id: number;

  @ApiBind({ column: 'name', includeJSON: true })
  name: string;

  @ApiBind({
    column: 'placements',
    model: (values) => values.map(value => new PlatformReservationPlacement(value)),
    includeJSON: true
  })
  placements: PlatformReservationPlacement[];

  @ApiBind({
    column: 'delivery_sets',
    model: (values) => values.map(value => new PlatformReservationDeliverySet(value)),
    includeJSON: true
  })
  deliverySets: PlatformReservationDeliverySet[];
}

/**
 * 配信PF予約登録に付属するSDKバージョングループを表すクラス.
 *
 * @class
 * @extends Common
 */
class PlatformReservationSdkVersionGroup extends Common {
  @ApiBind({ column: 'id' })
  id: number;

  @ApiBind({ column: 'name' })
  name: string;
}

/**
 * 配信PF予約登録に付属するOSグループを表すクラス.
 *
 * @class
 * @extends Common
 */
class PlatformReservationOsGroup extends Common {
  @ApiBind({ column: 'id' })
  id: number;

  @ApiBind({ column: 'name' })
  name: string;
}

/**
 * 配信PF予約登録に付属するプラットフォームを表すクラス.
 *
 * @class
 * @extends Common
 */
class PlatformReservationPlatform extends Common {
  @ApiBind({ column: 'id', includeJSON: true })
  id: number;

  @ApiBind({ column: 'ad_format', includeJSON: true })
  adFormat: AdFormatType;

  @ApiBind({ column: 'name', includeJSON: true })
  name: string;
}

/**
 * 配信PF予約登録を表すクラス.
 *
 * @class
 * @extends Common
 */
export class PlatformReservation extends Common {
  @ApiBind({ column: 'id' })
  id: number;

  @ApiBind({ column: 'name' })
  name: string;

  @ApiBind({ column: 'media_type' })
  mediaType: MediaType;

  @ApiBind({ column: 'scheduled_at', model: (value) => moment(value).toDate() })
  scheduledAt: Date;

  @ApiBind({
    column: 'platform',
    model: (value) => new PlatformReservationPlatform(value),
    includeJSON: true
  })
  platform: PlatformReservationPlatform;

  @ApiBind({ column: 'status' })
  status: PlatformReservationStatus;

  /**
   * @remarks nullの場合は項目ごと省略
   */
  @ApiBind({ column: 'error_reason' })
  errorReason?: string;

  @ApiBind({ column: 'updated_at' })
  updatedAt: string;

  @ApiBind({ column: 'ad_format' })
  adFormat: AdFormatType;

  @ApiBind({ column: 'os_group', model: (value) => new PlatformReservationOsGroup(value) })
  osGroup?: PlatformReservationOsGroup;

  @ApiBind({ column: 'sdk_version_group', model: (value) => new PlatformReservationSdkVersionGroup(value) })
  sdkVersionGroup?: PlatformReservationSdkVersionGroup;

  @ApiBind({ column: 'draw_method' })
  drawMethod: DeliveryControlDrawMethod;

  @ApiBind({
    column: 'placement_relations',
    model: (values) => values.map(value => new PlatformReservationPlacementRelation(value)),
    includeJSON: true
  })
  placementRelations: PlatformReservationPlacementRelation[];

  /**
   * 削除のAPIに送信するJSONに変換し返却する.
   *
   * @returns `id`と`updated_at`のプロパティが存在するオブジェクト
   */
  public toDeleteItemJSON() {
    return {
      id: this.convertJSONObject(this.id),
      updated_at: this.convertJSONObject(this.updatedAt),
    };
  }
}
